import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Footer from '@/components/Footer';
import TurnstileWidget from '@/components/TurnstileWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_ELEMENTS = [
  { type: 'badge', icon: 'fa-solid fa-microchip', color: '#1e293b', label: 'Microcontrollers', className: 'bb-float-1', style: { top: '10%', left: '0' } },
  { type: 'badge', icon: 'fa-solid fa-code', color: '#0891B2', label: 'Code & Logic', className: 'bb-float-2', style: { bottom: '10%', right: '15%' } },
  { type: 'gear', icon: 'fa-solid fa-gear', color: '#06B6D4', className: 'bb-spin', style: { top: '20%', right: '20%' } },
  { type: 'badge', icon: 'fa-solid fa-satellite-dish', color: '#f97316', label: 'IoT & Sensors', className: 'bb-float-3', style: { top: '5%', right: '5%' } },
  { type: 'gear', icon: 'fa-solid fa-gear', color: '#1e293b', className: 'bb-spin-fast', style: { top: '45%', right: '-5%' } },
  { type: 'gear', icon: 'fa-solid fa-gear', color: '#0891B2', className: 'bb-spin-reverse', style: { bottom: '5%', left: '15%' } },
  { type: 'badge', icon: 'fa-solid fa-brain', color: '#ef4444', label: 'AI Systems', className: 'bb-float-6', style: { top: '40%', left: '-10%' } },
];

const WHY_US = [
  { icon: 'fa-solid fa-hammer', title: 'Hands-on Learning', desc: 'Less theory, more doing. Every concept is tied directly to a practical build.', shape: 'bb-shape-circle', img: 'https://buildbots.in/why-us-1.jpg' },
  { icon: 'fa-solid fa-earth-americas', title: 'Real-World Projects', desc: 'Solve relevant problems, from automation to smart home prototypes.', shape: 'bb-shape-rounded', img: 'https://buildbots.in/why-us-2.jpg' },
  { icon: 'fa-solid fa-face-smile', title: 'Beginner Friendly', desc: 'No prior coding experience? No problem. We start from the absolute basics.', shape: 'bb-shape-pill', img: 'https://buildbots.in/why-us-3.jpg' },
  { icon: 'fa-solid fa-rocket', title: 'Innovation Focused', desc: 'We nurture creativity, encouraging students to invent their own mechanisms.', shape: 'bb-shape-blob', img: 'https://buildbots.in/why-us-4.jpg' },
];

const CARD_GRADIENTS = ['bb-gradient-light', 'bb-gradient-primary', 'bb-gradient-primary', 'bb-gradient-mix', 'bb-gradient-light', 'bb-gradient-mix', 'bb-gradient-primary'];
const CARD_ICONS = ['fa-solid fa-robot', 'fa-solid fa-futbol', 'fa-solid fa-person', 'fa-solid fa-cogs', 'fa-brands fa-python', 'fa-solid fa-microchip', 'fa-solid fa-laptop'];

const COUNTRIES = [
  { code: '+91', name: 'India' }, { code: '+1', name: 'USA' },
  { code: '+44', name: 'UK' }, { code: '+61', name: 'Australia' },
  { code: '+49', name: 'Germany' }, { code: '+33', name: 'France' },
  { code: '+81', name: 'Japan' }, { code: '+86', name: 'China' },
  { code: '+7', name: 'Russia' }, { code: '+55', name: 'Brazil' },
  { code: '+27', name: 'South Africa' }, { code: '+971', name: 'UAE' },
  { code: '+966', name: 'Saudi Arabia' }, { code: '+65', name: 'Singapore' },
  { code: '+60', name: 'Malaysia' }, { code: '+82', name: 'South Korea' },
  { code: '+39', name: 'Italy' }, { code: '+34', name: 'Spain' },
  { code: '+31', name: 'Netherlands' }, { code: '+46', name: 'Sweden' },
  { code: '+41', name: 'Switzerland' }, { code: '+64', name: 'New Zealand' },
  { code: '+353', name: 'Ireland' }, { code: '+52', name: 'Mexico' },
  { code: '+62', name: 'Indonesia' }, { code: '+66', name: 'Thailand' },
  { code: '+84', name: 'Vietnam' }, { code: '+63', name: 'Philippines' },
  { code: '+92', name: 'Pakistan' }, { code: '+880', name: 'Bangladesh' },
  { code: '+94', name: 'Sri Lanka' }, { code: '+977', name: 'Nepal' },
  { code: '+234', name: 'Nigeria' }, { code: '+254', name: 'Kenya' },
  { code: '+20', name: 'Egypt' }, { code: '+90', name: 'Turkey' },
  { code: '+48', name: 'Poland' }, { code: '+380', name: 'Ukraine' },
  { code: '+47', name: 'Norway' }, { code: '+45', name: 'Denmark' },
  { code: '+358', name: 'Finland' }, { code: '+43', name: 'Austria' },
  { code: '+32', name: 'Belgium' }, { code: '+351', name: 'Portugal' },
  { code: '+30', name: 'Greece' }, { code: '+972', name: 'Israel' },
];

const STEP_LABELS = ['Info', 'Email', 'Phone', 'Message'];

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function HomePage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [projects, setProjects] = useState([]);

  // Contact form state
  const [step, setStep] = useState(1);
  const [contactName, setContactName] = useState('');
  const [contactAge, setContactAge] = useState('');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpPreview, setOtpPreview] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [courseInterest, setCourseInterest] = useState('');
  const [message, setMessage] = useState('');
  const [formStatus, setFormStatus] = useState('');
  const [formError, setFormError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [honeypot] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const [siteSettings, setSiteSettings] = useState(null);

  const onCaptchaVerify = useCallback((token) => setCaptchaToken(token), []);
  const onCaptchaExpire = useCallback(() => setCaptchaToken(''), []);

  useEffect(() => {
    axios.get(`${API}/courses`).then(r => setCourses(r.data)).catch(() => {});
    axios.get(`${API}/projects`).then(r => setProjects(r.data)).catch(() => {});
    axios.get(`${API}/settings`).then(r => setSiteSettings(r.data)).catch(() => {});
    const hash = window.location.hash;
    if (hash) setTimeout(() => { document.getElementById(hash.replace('#', ''))?.scrollIntoView({ behavior: 'smooth' }); }, 300);
  }, []);

  // Resend countdown timer
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const validatePhone = (p) => /^\d{6,15}$/.test(p);

  // Step 1: Name + Age -> Continue
  const handleStep1Next = () => {
    if (!contactName.trim()) { setFormError('Please enter your name'); return; }
    if (!contactAge || isNaN(Number(contactAge)) || Number(contactAge) < 1 || Number(contactAge) > 120) {
      setFormError('Please enter a valid age (1-120)'); return;
    }
    setFormError(''); setStep(2);
  };

  // Step 2a: Email -> Send OTP (with loading and 30s resend timer)
  const handleSendOTP = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setFormError('Enter a valid email address'); return; }
    if (!captchaToken) { setFormError('Please complete the CAPTCHA verification first'); return; }
    setFormError(''); setFormStatus('sending');
    try {
      const { data } = await axios.post(`${API}/otp/send`, { email, captcha_token: captchaToken, honeypot });
      setOtpSent(true);
      setResendTimer(30);
      if (data.otp_preview) setOtpPreview(data.otp_preview);
      setFormStatus('');
    } catch (e) { setFormError(e.response?.data?.detail || 'Failed to send OTP'); setFormStatus(''); }
  };

  // Resend OTP
    // Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setFormError(''); setFormStatus('sending');
    setCaptchaResetKey(k => k + 1);
    try {
      const { data } = await axios.post(`${API}/otp/send`, { email, captcha_token: captchaToken, honeypot });
      setResendTimer(30);
      if (data.otp_preview) setOtpPreview(data.otp_preview);
      else setOtpPreview('');
      setOtpCode('');
      setFormStatus('');
    } catch (e) { setFormError(e.response?.data?.detail || 'Failed to resend OTP'); setFormStatus(''); }
  };

  // Step 2b: Verify OTP
  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) { setFormError('Enter the 6-digit OTP sent to your email'); return; }
    setFormError(''); setFormStatus('verifying');
    try {
      await axios.post(`${API}/otp/verify`, { email, otp: otpCode });
      setEmailVerified(true); setStep(3); setFormStatus('');
    } catch (e) { setFormError(e.response?.data?.detail || 'Invalid OTP'); setFormStatus(''); }
  };

  // Step 3: Phone -> Continue
  const handlePhoneNext = () => {
    if (!validatePhone(phone)) { setFormError('Enter a valid phone number (6-15 digits)'); return; }
    setFormError(''); setStep(4);
  };

  // Step 4: Submit
  const handleSubmitContact = async () => {
    const fullMessage = courseInterest
      ? `Interested in: ${courseInterest}\n\n${message}`.trim()
      : message;
    if (!fullMessage.trim()) { setFormError('Please write a message or select a course'); return; }
    setFormError(''); setFormStatus('sending');
    try {
      await axios.post(`${API}/contact`, {
        name: contactName, email, age: contactAge,
        phone, country_code: countryCode,
        message: fullMessage, email_verified: emailVerified,
        captcha_token: captchaToken, honeypot
      });
      setFormStatus('success'); setStep(5);
    } catch (e) { setFormError(e.response?.data?.detail || 'Failed to send'); setFormStatus(''); }
  };

  const BG_COLORS = ['bg-p1', 'bg-p2', 'bg-p3', 'bg-p4'];

  return (
    <div className="min-h-screen relative">
      <div className="memphis-shape shape-circle-1" />
      <div className="memphis-shape shape-circle-2" />
      <div className="memphis-shape shape-dots" style={{ position: 'fixed', top: '10%', right: '5%', zIndex: 0, pointerEvents: 'none' }} />
      <div className="memphis-shape shape-dots" style={{ position: 'fixed', bottom: '20%', left: '5%', zIndex: 0, pointerEvents: 'none' }} />
      <div className="shape-zigzag-1"><svg viewBox="0 0 100 20" fill="none" width="100"><path d="M0 10 L20 0 L40 20 L60 0 L80 20 L100 10" stroke="#0891B2" strokeWidth="4" strokeLinejoin="round"/></svg></div>
      <div className="shape-zigzag-2"><svg viewBox="0 0 100 20" fill="none" width="100"><path d="M0 10 L20 0 L40 20 L60 0 L80 20 L100 10" stroke="#000000" strokeWidth="4" strokeLinejoin="round"/></svg></div>

      {/* HERO */}
      <section id="home" className="bb-hero" data-testid="hero-section">
        <div className="w-[90%] max-w-[1200px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-10">
          <motion.div className="flex-1" initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp}><span className="bb-badge" data-testid="hero-badge">All Ages</span></motion.div>
            <motion.h1 variants={fadeUp} className="bb-headline">Explore AI, Robotics &amp; <span>Electronics</span></motion.h1>
            <motion.p variants={fadeUp} className="bb-subheading">Unleash your creativity with hands-on learning through Arduino, robotics kits, and practical coding. Build the future with <strong style={{ color: 'var(--text)' }}>BuildBotics</strong>.</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-5">
              <a href="#contact" className="bb-btn bb-btn-primary" data-testid="hero-workshop-btn">Join a Workshop <i className="fa-solid fa-arrow-right" /></a>
              <a href="#projects" className="bb-btn bb-btn-secondary" data-testid="hero-projects-btn">View Projects</a>
            </motion.div>
          </motion.div>
          <motion.div className="flex-1 relative hidden lg:block" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.3 }}>
            <div className="relative w-full" style={{ paddingBottom: '100%' }}>
              {HERO_ELEMENTS.map((el, i) => (
                <div key={i} className={`bb-glass-card ${el.type === 'gear' ? 'bb-circle' : ''} ${el.className}`} style={el.style} data-testid={`hero-element-${i}`}>
                  <i className={el.icon} style={{ color: el.color, fontSize: '1.5rem' }} />{el.label && <span>{el.label}</span>}
                </div>
              ))}
              <div className="bb-robot-wrapper bb-pulse" data-testid="hero-robot">
                <img src="https://api.dicebear.com/7.x/bottts/svg?seed=Felix&backgroundColor=transparent" alt="Robot" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="bb-section" data-testid="about-section">
        <div className="w-[90%] max-w-[1200px] mx-auto flex flex-col lg:flex-row items-center gap-16">
          <motion.div className="flex-1" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="bb-about-image-wrapper"><img src="https://buildbots.in/about-img.jpg" alt="Mentoring" data-testid="about-image" /></div>
          </motion.div>
          <motion.div className="flex-[1.2]" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="bb-section-title">Empowering the Innovators of Tomorrow</motion.h2>
            <motion.p variants={fadeUp} className="text-[1.1rem] text-[var(--text-light)] mt-6 mb-6">We teach practical STEM skills that go beyond textbooks. At BuildBotics, students dive into the world of technology by building things that matter.</motion.p>
            <motion.div variants={fadeUp} className="space-y-4">
              {[{ i: 'fa-solid fa-robot', t: 'Robotics:', d: 'Design and assemble real working robots.' },
                { i: 'fa-solid fa-microchip', t: 'Arduino & IoT:', d: 'Connect the physical world using sensors and code.' },
                { i: 'fa-solid fa-lightbulb', t: 'Problem-Solving:', d: 'Apply logic to craft innovative solutions.' }
              ].map((f, idx) => (
                <div key={idx} className="bb-about-highlight" data-testid={`about-feature-${idx}`}>
                  <i className={f.i} /><p><strong>{f.t}</strong> {f.d}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section id="programs" className="bb-section bb-bg-light" data-testid="programs-section">
        <div className="w-[90%] max-w-[1200px] mx-auto">
          <div className="text-center mb-12"><h2 className="bb-section-title text-center">Our Programs</h2><p className="text-[1.1rem] text-[var(--text-light)] mt-6">Structured learning paths for all skill levels.</p></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {courses.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bb-course-card" onClick={() => navigate(`/course/${c.id}`)} data-testid={`program-card-${c.slug || c.id}`}>
                <div className={`bb-card-icon ${CARD_GRADIENTS[i % CARD_GRADIENTS.length]}`}><i className={CARD_ICONS[i % CARD_ICONS.length]} /></div>
                <span className="bb-grade-pill">{c.level}</span>
                <h3 className="text-[1.5rem] font-bold mb-4">{c.title}</h3>
                <p className="text-[var(--text-light)] mb-6 min-h-[60px]">{c.short_description || c.description?.slice(0, 150) + '...'}</p>
                <button className="bb-link-btn" onClick={e => { e.stopPropagation(); navigate(`/course/${c.id}`); }}>Learn More <i className="fa-solid fa-angle-right" /></button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS (from DB) */}
      <section id="projects" className="bb-section" data-testid="projects-section">
        <div className="w-[90%] max-w-[1200px] mx-auto">
          <div className="text-center mb-12"><h2 className="bb-section-title text-center">Student Showcase</h2><p className="text-[1.1rem] text-[var(--text-light)] mt-6">Real-world projects built by our incredible students.</p></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {projects.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bb-project-card" data-testid={`showcase-card-${i}`}>
                <div className={`bb-project-img ${p.bg_color || BG_COLORS[i % 4]}`}><img src={p.image_url} alt={p.title} /></div>
                <div className="p-6"><h3 className="text-[1.2rem] font-bold mb-2">{p.title}</h3><p className="text-[0.95rem] text-[var(--text-light)]">{p.description}</p></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bb-section bb-bg-light" data-testid="why-section">
        <div className="w-[90%] max-w-[1200px] mx-auto">
          <div className="text-center mb-12"><h2 className="bb-section-title text-center">Why Learn With Us?</h2></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {WHY_US.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bb-feature-item text-center p-5" data-testid={`why-card-${i}`}>
                <div className={`bb-feature-icon ${item.shape}`}><i className={item.icon} /></div>
                <h4 className="text-[1.3rem] font-bold mb-4">{item.title}</h4>
                <p className="text-[var(--text-light)]">{item.desc}</p>
                <div className="bb-feature-img-wrapper"><img src={item.img} alt={item.title} /></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT - Step by Step Email OTP */}
      <section id="contact" className="bb-section" data-testid="contact-section">
        <div className="w-[90%] max-w-[1200px] mx-auto">
          <div className="bb-contact-wrapper">
            <div className="bb-contact-info">
              <h2 className="bb-section-title" style={{ color: 'white' }}>Get in Touch</h2>
              <p className="mt-6 mb-8 text-white/90">Verify your email to send us a message. Each step unlocks the next.</p>
              <div className="space-y-6">
                {[{ icon: 'fa-regular fa-envelope', t: 'Email', v: siteSettings?.email || 'hello@buildbotics.in' },
                  { icon: 'fa-brands fa-whatsapp', t: 'WhatsApp', v: siteSettings?.whatsapp || 'Chat with us anytime', link: siteSettings?.whatsapp_link },
                  { icon: 'fa-brands fa-instagram', t: 'Instagram', v: siteSettings?.instagram || '@buildbot.in', link: siteSettings?.instagram_link || 'https://www.instagram.com/buildbot.in/' },
                  { icon: 'fa-solid fa-globe', t: 'Website', v: siteSettings?.website || 'https://www.buildbotics.in', link: siteSettings?.website || 'https://www.buildbotics.in' }
                ].map((c, i) => (
                  <div key={i} className="flex items-center gap-5" data-testid={`contact-info-${i}`}>
                    <div className="bb-contact-icon"><i className={c.icon} /></div>
                    <div><h5 className="font-bold text-sm mb-1">{c.t}</h5>
                      {c.link ? <a href={c.link} target="_blank" rel="noopener noreferrer" className="text-sm opacity-90 underline">{c.v}</a> : <span className="text-sm opacity-90">{c.v}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bb-contact-memphis" />
            </div>

            {/* Step-by-step Form */}
            <div className="bb-contact-form">
              {/* Progress indicator */}
              <div className="flex items-center gap-2 mb-8" data-testid="form-progress">
                {STEP_LABELS.map((label, idx) => {
                  const s = idx + 1;
                  return (
                    <div key={s} className="flex items-center gap-2">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                          step > s ? 'bg-green-500 border-green-500 text-white' :
                          step === s ? 'bg-[#0891B2] border-[#0891B2] text-white' :
                          'bg-gray-100 border-gray-300 text-gray-400'
                        }`}>{step > s ? <i className="fa-solid fa-check text-xs" /> : s}</div>
                        <span className={`text-[10px] mt-1 ${step >= s ? 'text-[#0891B2]' : 'text-gray-400'}`}>{label}</span>
                      </div>
                      {s < 4 && <div className={`w-8 h-0.5 mb-4 ${step > s ? 'bg-green-500' : 'bg-gray-200'}`} />}
                    </div>
                  );
                })}
              </div>

              {formError && <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg mb-4" data-testid="form-error">{formError}</div>}

              {/* Honeypot */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
              <input type="text" name="fax_number"      tabIndex={-1} autoComplete="new-password"
              value="" onChange={() => {}} readOnly />
              </div>

              {/* Step 1: Name + Age */}
              <div style={{ opacity: step >= 1 ? 1 : 0.3, pointerEvents: step >= 1 ? 'auto' : 'none' }}>
                <div className="bb-form-group">
                  <label>Full Name</label>
                  <input type="text" value={contactName} onChange={e => setContactName(e.target.value)}
                    placeholder="Your Name" className="bb-form-input" disabled={step > 1} data-testid="contact-name-input" />
                </div>
                <div className="bb-form-group">
                  <label>Age</label>
                  <input type="number" value={contactAge} onChange={e => setContactAge(e.target.value)}
                    placeholder="e.g. 14" className="bb-form-input" min="1" max="120" disabled={step > 1} data-testid="contact-age-input" />
                </div>
                {step === 1 && (
                  <button onClick={handleStep1Next}
                    className="bb-btn bb-btn-primary bb-btn-block !mb-4" data-testid="step1-next-btn">
                    Continue <i className="fa-solid fa-arrow-right" />
                  </button>
                )}
              </div>

              {/* Step 2: Email + OTP Verification */}
              {step >= 2 && (
                <div style={{ opacity: step >= 2 ? 1 : 0.3, pointerEvents: step === 2 ? 'auto' : 'none' }} className="mt-2">
                  <div className="bb-form-group">
                    <label>Email Address</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com" className="bb-form-input" disabled={otpSent} data-testid="contact-email-input" />
                  </div>

                  {!otpSent && step === 2 && (
                    <>
                      <TurnstileWidget onVerify={onCaptchaVerify} onExpire={onCaptchaExpire} resetKey={captchaResetKey} />
                      <button onClick={handleSendOTP} disabled={formStatus === 'sending'}
                        className="bb-btn bb-btn-primary bb-btn-block !mb-4" data-testid="send-otp-btn">
                        {formStatus === 'sending' ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending OTP...
                          </span>
                        ) : <>Send OTP to Email <i className="fa-solid fa-paper-plane" /></>}
                      </button>
                    </>
                  )}

                  {/* Loading state while OTP is being sent */}
                  {formStatus === 'sending' && otpSent && (
                    <div className="flex flex-col items-center py-6 gap-3" data-testid="otp-loading">
                      <div className="w-8 h-8 border-3 border-[#0891B2] border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm text-gray-500">Sending OTP to your email...</p>
                    </div>
                  )}

                  {otpSent && !emailVerified && formStatus !== 'sending' && (
                    <>
                      {otpPreview && (
                        <div className="bg-cyan-50 border border-cyan-200 text-cyan-700 text-sm px-4 py-3 rounded-lg mb-3" data-testid="otp-preview">
                          <i className="fa-solid fa-info-circle mr-2" />Demo OTP: <strong>{otpPreview}</strong> <span className="text-xs opacity-70">(Fallback - email delivery had an issue)</span>
                        </div>
                      )}
                      {!otpPreview && (
                        <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-lg mb-3" data-testid="otp-sent-msg">
                          <i className="fa-solid fa-envelope-circle-check mr-2" />OTP sent! Check your email inbox (and spam folder).
                        </div>
                      )}
                      <div className="bb-form-group">
                        <label>Enter 6-Digit OTP</label>
                        <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000" className="bb-form-input !text-center !text-2xl !tracking-[0.5em] !font-mono"
                          maxLength={6} data-testid="otp-input" />
                      </div>
                      {step === 2 && (
                        <div className="flex flex-col gap-2 mb-4">
                          <button onClick={handleVerifyOTP} disabled={formStatus === 'verifying'}
                            className="bb-btn bb-btn-primary bb-btn-block" data-testid="verify-otp-btn">
                            {formStatus === 'verifying' ? 'Verifying...' : <>Verify Email <i className="fa-solid fa-shield-halved" /></>}
                          </button>
                          <button onClick={handleResendOTP} disabled={resendTimer > 0 || formStatus === 'sending'}
                            className="text-sm text-center text-gray-500 hover:text-[#0891B2] disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="resend-otp-btn">
                            {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {emailVerified && <div className="flex items-center gap-2 text-green-600 text-sm mb-4" data-testid="email-verified-msg"><i className="fa-solid fa-circle-check" /> Email verified!</div>}
                </div>
              )}

              {/* Step 3: Phone Number (no OTP) */}
              {step >= 3 && (
                <div style={{ opacity: step >= 3 ? 1 : 0.3, pointerEvents: step === 3 ? 'auto' : 'none' }} className="mt-2">
                  <div className="bb-form-group">
                    <label>Phone Number</label>
                    <div className="flex gap-2">
                      <select value={countryCode} onChange={e => setCountryCode(e.target.value)}
                        className="bb-form-input !w-[130px] !flex-shrink-0" disabled={step > 3} data-testid="country-code-select">
                        {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.code} {c.name}</option>)}
                      </select>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                        placeholder="9999999999" className="bb-form-input !flex-1" disabled={step > 3} maxLength={15} data-testid="contact-phone-input" />
                    </div>
                  </div>
                  {step === 3 && (
                    <>
                      <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm px-4 py-3 rounded-lg mb-4" data-testid="phone-recheck-note">
                        <i className="fa-solid fa-triangle-exclamation mr-2" />Please double-check your phone number before continuing. We'll use it to contact you.
                      </div>
                      <button onClick={handlePhoneNext}
                        className="bb-btn bb-btn-primary bb-btn-block !mb-4" data-testid="phone-next-btn">
                        Continue <i className="fa-solid fa-arrow-right" />
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Step 4: Course Interest + Message */}
              {step >= 4 && (
                <div style={{ opacity: step >= 4 ? 1 : 0.3, pointerEvents: step === 4 ? 'auto' : 'none' }} className="mt-2">
                  <div className="bb-form-group">
                    <label>Which course are you interested in?</label>
                    <select value={courseInterest} onChange={e => setCourseInterest(e.target.value)}
                      className="bb-form-input" data-testid="course-interest-select">
                      <option value="">-- Select a course (optional) --</option>
                      {courses.map(c => <option key={c.id} value={c.title}>{c.title}</option>)}
                      <option value="Other">Other / Not sure yet</option>
                    </select>
                    <span className="text-xs text-gray-400 mt-1 block">e.g. "Line Following Robot", "Python Masterclass", "Robotics Masterclass"</span>
                  </div>
                  <div className="bb-form-group">
                    <label>Message</label>
                    <textarea rows={4} value={message} onChange={e => setMessage(e.target.value)}
                      placeholder="I'm interested in the robotics program for my 12-year-old..." className="bb-form-input" data-testid="contact-message-input" />
                  </div>
                  <button onClick={handleSubmitContact} disabled={formStatus === 'sending'}
                    className="bb-btn bb-btn-primary bb-btn-block" data-testid="contact-submit-btn">
                    {formStatus === 'sending' ? 'Sending...' : <>Send Message <i className="fa-regular fa-paper-plane" /></>}
                  </button>
                </div>
              )}

                            {/* Step 5: Success */}
              {step === 5 && (
                <div className="text-center py-8" data-testid="contact-success-msg">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <i className="fa-solid fa-check text-green-600 text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--text)] mb-2">Message Sent!</h3>
                  <p className="text-[var(--text-light)]">We'll get back to you soon. Thank you!</p>
                  <button
                    onClick={() => {
                      setStep(1);
                      setContactName('');
                      setContactAge('');
                      setEmail('');
                      setOtpCode('');
                      setOtpSent(false);
                      setOtpPreview('');
                      setEmailVerified(false);
                      setPhone('');
                      setCourseInterest('');
                      setMessage('');
                      setFormStatus('');
                      setFormError('');
                      setCaptchaToken('');
                      setCaptchaResetKey(k => k + 1);
                      setResendTimer(0);
                    }}
                    className="bb-btn bb-btn-primary mt-4"
                    data-testid="new-enquiry-btn"
                  >
                    New Enquiry <i className="fa-solid fa-plus" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
